/**
 * React glue for lib/webPush (TRUS-668).
 *
 * useWebPushSync — mount once per signed-in shell (staff AppContent, member
 *   portal layout). On each app load with permission already granted it gets
 *   the FCM token again (token refresh) and registers it; never prompts.
 * useWebPushControl — status + enable/disable for a button or banner.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  disableWebPush,
  enableWebPush,
  getLastWebPushFailure,
  getWebPushStatus,
  isWebPushSupported,
  memberTransport,
  onWebPushStatusChange,
  staffTransport,
  syncWebPush,
  type WebPushFailure,
  type WebPushStatus,
  type WebPushTransport,
} from '@/lib/webPush';
import { handlePushNavigateMessage } from '@/lib/pushNavigation';

export type WebPushPortal = 'staff' | 'member';

export function transportFor(portal: WebPushPortal): WebPushTransport {
  return portal === 'member' ? memberTransport() : staffTransport();
}

/** Re-register this browser after sign-in (silent; no permission prompt). */
export function useWebPushSync(active: boolean, portal: WebPushPortal): void {
  const done = useRef(false);
  useEffect(() => {
    if (!active) {
      done.current = false;
      return;
    }
    if (done.current || !isWebPushSupported()) return;
    done.current = true;
    void syncWebPush(transportFor(portal));
  }, [active, portal]);

  // The push service rotated this browser's subscription and the service
  // worker re-subscribed. The FCM token is tied to the subscription, so get the
  // token again (Firebase updates it) and register it (the worker has no auth).
  useEffect(() => {
    if (!active || !isWebPushSupported()) return;
    const onMessage = (event: MessageEvent) => {
      const msg = event.data as { type?: string } | null;
      if (msg?.type !== 'WEAVEIN_PUSH_SUBSCRIPTION_CHANGED') return;
      void syncWebPush(transportFor(portal));
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [active, portal]);
}

export interface WebPushControl {
  status: WebPushStatus | 'loading';
  /** Why the last attempt on this page failed (status 'error', or 'denied' from a click). */
  failure: WebPushFailure | null;
  busy: boolean;
  enable: () => Promise<WebPushStatus>;
  disable: () => Promise<void>;
}

export function useWebPushControl(portal: WebPushPortal): WebPushControl {
  const [status, setStatus] = useState<WebPushStatus | 'loading'>(() =>
    isWebPushSupported() ? 'loading' : 'unsupported',
  );
  const [busy, setBusy] = useState(false);
  // A failure from earlier on this page (a sync that ran before this mounted).
  const [failure, setFailure] = useState<WebPushFailure | null>(() => getLastWebPushFailure());
  // One transport per control: lib/webPush remembers the config it fetched per
  // transport, so a click can raise the permission prompt before any await.
  const transport = useMemo(() => transportFor(portal), [portal]);

  useEffect(() => {
    if (!isWebPushSupported()) return;
    let cancelled = false;
    // A sync / click result that arrives while the status check is still in
    // flight is newer than it: the check must not overwrite it (a slow check
    // could otherwise put a just-registered browser back to "default" and show
    // the banner again).
    let heard = false;
    getWebPushStatus(transport).then((s) => {
      if (cancelled || heard) return;
      // The browser has no registration but a sync on this page already failed:
      // say why instead of offering a plain "Turn on" again.
      const earlier = getLastWebPushFailure();
      setStatus(s === 'default' && earlier && earlier.kind !== 'blocked' ? 'error' : s);
    });
    // A silent sync (or another control on the page) registered this browser,
    // or failed.
    const off = onWebPushStatusChange((s, f) => {
      if (cancelled) return;
      heard = true;
      setStatus(s);
      setFailure(s === 'error' || s === 'denied' ? (f ?? getLastWebPushFailure()) : null);
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [transport]);

  // Not async on purpose: enableWebPush raises the permission prompt before its
  // first await, which keeps it inside the click (Safari requires that).
  const enable = useCallback(() => {
    setBusy(true);
    const pending = enableWebPush(transport);
    return pending
      .then((s) => {
        setStatus(s);
        setFailure(s === 'error' || s === 'denied' ? getLastWebPushFailure() : null);
        return s;
      })
      .finally(() => setBusy(false));
  }, [transport]);

  const disable = useCallback(async () => {
    setBusy(true);
    try {
      await disableWebPush(transport);
      setStatus('default');
    } finally {
      setBusy(false);
    }
  }, [transport]);

  return { status, failure, busy, enable, disable };
}

/**
 * Route a clicked notification in-app instead of reloading the window (a reload
 * would drop a call). The service worker posts `weavein:navigate`; `route`
 * returns true when it handled the URL, and the worker only falls back to a
 * real navigation when nobody acks. Top-level main window only.
 */
export function useWebPushNavigation(active: boolean, route: (url: string) => boolean): void {
  const routeRef = useRef(route);
  routeRef.current = route;
  useEffect(() => {
    if (!active || !isWebPushSupported()) return;
    const onMessage = (event: MessageEvent) => {
      handlePushNavigateMessage(event, (url) => routeRef.current(url));
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [active]);
}
